// Fixture-backed stand-in for api.ts + sse.ts, active when VITE_MOCK=1 (see src/backend.ts).
// ~12 players, a live match mid-game, a handful of finished ones, and rankings derived straight
// from the players' fixture ratings. Point/undo calls mutate the fixture naively (increment or
// decrement a score, flip games/status when a naive threshold is crossed) — there is no real
// scoring engine here, just enough state-shuffling to click every screen. Subscribers to
// subscribeTableEvents() are replayed a fresh `state` after every mutating call, matching the
// real SSE contract's "sent on connect and after every change."
import type {
  ApiErrorPayload,
  HeadToHeadResponse,
  IdentityResolveResponse,
  Match,
  MatchType,
  Player,
  PlayerDetail,
  RankingsResponse,
  RatingChange,
  RatingHistoryEntry,
  RatingPool,
  RatingSummary,
  Side,
  TableState,
  Tournament,
  TournamentBracketSlot,
  TournamentDetail,
  TournamentStatus,
  VersusResponse,
} from './types'
import { DEFAULT_SERVE_EVERY, SERVE_EVERY_OPTIONS } from './types'
import type { CreateMatchPayload as _CreateMatchPayload, GetMatchesParams } from './api'

export { ApiRequestError } from './api'
import { ApiRequestError } from './api'

// A real backend hands every response over the wire as independent JSON — no two ticks ever
// share an object reference. This mock mutates fixtures in place internally (see
// finishGameIfNaive etc.), so every value handed back to a caller (a REST return, or a broadcast
// `state` payload) MUST be a deep, independent snapshot — otherwise a consumer holding an "old"
// reference from a previous tick would see it silently change underneath it as the mock keeps
// mutating the shared object, and Vue's reactivity (which tracks ref/reactive reassignment, not
// arbitrary object mutation) would never notice. An early version of this file returned live
// references directly and abandon/undo/points appeared to silently no-op — snapshot everything.
function snapshot<T>(value: T): T {
  return structuredClone(value)
}

function err(code: string, message: string): ApiRequestError {
  const payload: ApiErrorPayload = { error: message, code }
  const status = code === 'unknown_badge' || code === 'unranked' ? 400 : code.includes('not_found') ? 404 : 409
  return new ApiRequestError(payload, status)
}

// ---------------------------------------------------------------------------------------------
// Fixtures
// ---------------------------------------------------------------------------------------------

function iso(minutesAgo: number): string {
  return new Date(Date.now() - minutesAgo * 60_000).toISOString()
}

function ranked(rating: number, rd: number, volatility: number, matches: number, wins: number, losses: number): RatingSummary {
  return { rating, rd, volatility, matches, wins, losses, provisional: rd > 110, ranked: matches > 0 }
}

function unranked(): RatingSummary {
  return { rating: 1500, rd: 350, volatility: 0.06, matches: 0, wins: 0, losses: 0, provisional: true, ranked: false }
}

interface PlayerSeed {
  id: number
  first: string
  last: string
  email: string
  createdMinsAgo: number
  lastSeenMinsAgo: number
  rapid?: RatingSummary
  classical?: RatingSummary
}

const SEEDS: PlayerSeed[] = [
  { id: 1, first: 'Ana', last: 'García', email: 'ana.garcia@kombo.so', createdMinsAgo: 60 * 24 * 90, lastSeenMinsAgo: 3, rapid: ranked(1612, 68, 0.059, 24, 16, 8), classical: ranked(1540, 95, 0.06, 6, 3, 3) },
  { id: 2, first: 'Marc', last: 'Puig', email: 'marc.puig@kombo.so', createdMinsAgo: 60 * 24 * 85, lastSeenMinsAgo: 3, rapid: ranked(1578, 71, 0.059, 22, 12, 10) },
  { id: 3, first: 'Laura', last: 'Soler', email: 'laura.soler@kombo.so', createdMinsAgo: 60 * 24 * 80, lastSeenMinsAgo: 40, rapid: ranked(1655, 55, 0.058, 31, 21, 10), classical: ranked(1602, 60, 0.058, 18, 11, 7) },
  { id: 4, first: 'Jordi', last: 'Vidal', email: 'jordi.vidal@kombo.so', createdMinsAgo: 60 * 24 * 70, lastSeenMinsAgo: 120, rapid: ranked(1489, 130, 0.061, 5, 2, 3) },
  { id: 5, first: 'Nuria', last: 'Ferrer', email: 'nuria.ferrer@kombo.so', createdMinsAgo: 60 * 24 * 60, lastSeenMinsAgo: 200, rapid: ranked(1521, 82, 0.06, 14, 8, 6), classical: ranked(1498, 140, 0.062, 3, 1, 2) },
  { id: 6, first: 'Pau', last: 'Roig', email: 'pau.roig@kombo.so', createdMinsAgo: 60 * 24 * 55, lastSeenMinsAgo: 1440 },
  { id: 7, first: 'Marta', last: 'Serra', email: 'marta.serra@kombo.so', createdMinsAgo: 60 * 24 * 50, lastSeenMinsAgo: 300, rapid: ranked(1445, 102, 0.06, 9, 3, 6) },
  { id: 8, first: 'David', last: 'Costa', email: 'david.costa@kombo.so', createdMinsAgo: 60 * 24 * 45, lastSeenMinsAgo: 600, rapid: ranked(1503, 350, 0.06, 1, 0, 1) },
  { id: 9, first: 'Elena', last: 'Ruiz', email: 'elena.ruiz@kombo.so', createdMinsAgo: 60 * 24 * 40, lastSeenMinsAgo: 20, rapid: ranked(1590, 61, 0.059, 19, 13, 6), classical: ranked(1555, 88, 0.06, 8, 5, 3) },
  { id: 10, first: 'Toni', last: 'Blanch', email: 'toni.blanch@kombo.so', createdMinsAgo: 60 * 24 * 30, lastSeenMinsAgo: 4000 },
  { id: 11, first: 'Clara', last: 'Bosch', email: 'clara.bosch@kombo.so', createdMinsAgo: 60 * 24 * 20, lastSeenMinsAgo: 90, rapid: ranked(1467, 118, 0.06, 6, 2, 4) },
  { id: 12, first: 'Xavi', last: 'Molins', email: 'xavi.molins@kombo.so', createdMinsAgo: 60 * 24 * 10, lastSeenMinsAgo: 15, rapid: ranked(1533, 76, 0.059, 15, 9, 6) },
]

const players: Player[] = SEEDS.map((s) => ({
  id: s.id,
  displayName: `${s.first} ${s.last}`,
  firstName: s.first,
  lastName: s.last,
  email: s.email,
  pictureUrl: null,
  isGuest: false,
  identitySource: 'kombo',
  createdAt: iso(s.createdMinsAgo),
  lastSeenAt: iso(s.lastSeenMinsAgo),
  ratings: { rapid: s.rapid ?? unranked(), classical: s.classical ?? unranked() },
}))

function findPlayer(id: number): Player {
  const p = players.find((pl) => pl.id === id)
  if (!p) throw err('player_not_found', 'Unknown player')
  return p
}

const matchTypes: MatchType[] = [
  { code: 'rapid11', name: 'Rapid 11', pointsToWin: 11, serveRotation: 2, winBy: 2, ratingPool: 'rapid', allowedBestOf: [1, 3], defaultBestOf: 1 },
  { code: 'classic21', name: 'Classic 21', pointsToWin: 21, serveRotation: 5, winBy: 2, ratingPool: 'classical', allowedBestOf: [1, 3], defaultBestOf: 1 },
]

function findMatchType(code: string): MatchType {
  const mt = matchTypes.find((m) => m.code === code)
  if (!mt) throw err('unknown_match_type', 'Unknown match type')
  return mt
}

let nextMatchId = 6000

function emptyGame(index: number, left: Player, right: Player, firstServer: Side, startedMinsAgo: number): Match['games'][number] {
  return {
    index,
    status: 'live',
    scores: { left: 0, right: 0 },
    players: { left, right },
    winnerPlayerId: null,
    firstServer,
    startedAt: iso(startedMinsAgo),
    finishedAt: null,
  }
}

// A handful of finished matches (some rapid, some classic, a best-of-3, an abandon) so Past
// Matches / Versus / Player screens have something to show.
function buildFinishedMatch(opts: {
  id: number
  leftId: number
  rightId: number
  matchTypeCode: string
  bestOf: number
  leftGameScores: number[]
  rightGameScores: number[]
  finishedMinsAgo: number
  abandoned?: boolean
}): Match {
  const left = findPlayer(opts.leftId)
  const right = findPlayer(opts.rightId)
  const mt = findMatchType(opts.matchTypeCode)
  const games: Match['games'] = opts.leftGameScores.map((ls, i) => {
    const rs = opts.rightGameScores[i]
    const winner = ls > rs ? left.id : right.id
    return {
      index: i,
      status: 'finished',
      scores: { left: ls, right: rs },
      players: i % 2 === 0 ? { left, right } : { left: right, right: left },
      winnerPlayerId: winner,
      firstServer: i % 2 === 0 ? 'left' : 'right',
      startedAt: iso(opts.finishedMinsAgo + (opts.leftGameScores.length - i) * 6),
      finishedAt: iso(opts.finishedMinsAgo + (opts.leftGameScores.length - i - 1) * 6),
    }
  })
  const gamesWon: Record<string, number> = {}
  for (const g of games) {
    if (g.winnerPlayerId != null) gamesWon[g.winnerPlayerId] = (gamesWon[g.winnerPlayerId] ?? 0) + 1
  }
  const winnerPlayerId = opts.abandoned ? null : (gamesWon[left.id] ?? 0) > (gamesWon[right.id] ?? 0) ? left.id : right.id
  const ratingChanges: Record<string, RatingChange> | null = opts.abandoned
    ? null
    : {
        [left.id]: { pool: mt.ratingPool, before: left.ratings[mt.ratingPool].rating - 8, after: left.ratings[mt.ratingPool].rating, delta: 8 },
        [right.id]: { pool: mt.ratingPool, before: right.ratings[mt.ratingPool].rating + 8, after: right.ratings[mt.ratingPool].rating, delta: -8 },
      }
  return {
    id: opts.id,
    table: 'main',
    matchType: mt,
    bestOf: opts.bestOf,
    serveEvery: DEFAULT_SERVE_EVERY,
    status: opts.abandoned ? 'abandoned' : 'finished',
    startedAt: iso(opts.finishedMinsAgo + games.length * 6 + 2),
    finishedAt: iso(opts.finishedMinsAgo),
    players: { left, right },
    currentPlayers: games.length % 2 === 0 ? { left, right } : { left: right, right: left },
    games,
    currentGameIndex: null,
    gamesWon,
    server: null,
    serverPlayerId: null,
    banner: null,
    winnerPlayerId,
    ratingChanges,
    pointsCount: games.reduce((sum, g) => sum + g.scores.left + g.scores.right, 0),
    lastPoint: null,
    tournamentId: null,
    tournamentSlotId: null,
    fastMode: false,
  }
}

const finishedMatches: Match[] = [
  buildFinishedMatch({ id: 5901, leftId: 3, rightId: 1, matchTypeCode: 'rapid11', bestOf: 3, leftGameScores: [11, 9, 11], rightGameScores: [7, 11, 6], finishedMinsAgo: 25 }),
  buildFinishedMatch({ id: 5902, leftId: 9, rightId: 2, matchTypeCode: 'rapid11', bestOf: 1, leftGameScores: [11], rightGameScores: [8], finishedMinsAgo: 95 }),
  buildFinishedMatch({ id: 5903, leftId: 12, rightId: 5, matchTypeCode: 'classic21', bestOf: 1, leftGameScores: [19], rightGameScores: [21], finishedMinsAgo: 180 }),
  buildFinishedMatch({ id: 5904, leftId: 1, rightId: 9, matchTypeCode: 'rapid11', bestOf: 3, leftGameScores: [11, 6], rightGameScores: [4, 11], finishedMinsAgo: 260, abandoned: true }),
  buildFinishedMatch({ id: 5905, leftId: 3, rightId: 4, matchTypeCode: 'rapid11', bestOf: 1, leftGameScores: [11], rightGameScores: [3], finishedMinsAgo: 400 }),
  buildFinishedMatch({ id: 5906, leftId: 11, rightId: 7, matchTypeCode: 'rapid11', bestOf: 3, leftGameScores: [9, 11, 8], rightGameScores: [11, 5, 11], finishedMinsAgo: 600 }),
]

function buildLiveMatch(): Match {
  const left = findPlayer(1)
  const right = findPlayer(2)
  const mt = findMatchType('rapid11')
  const game = emptyGame(0, left, right, 'left', 6)
  game.scores = { left: 7, right: 5 }
  return {
    id: 5999,
    table: 'main',
    matchType: mt,
    bestOf: 3,
    serveEvery: DEFAULT_SERVE_EVERY,
    status: 'live',
    startedAt: iso(6),
    finishedAt: null,
    players: { left, right },
    currentPlayers: { left, right },
    games: [game],
    currentGameIndex: 0,
    gamesWon: {},
    server: 'right',
    serverPlayerId: right.id,
    banner: null,
    winnerPlayerId: null,
    ratingChanges: null,
    pointsCount: 12,
    lastPoint: { side: 'left', source: 'touch', at: iso(0.2) },
    tournamentId: null,
    tournamentSlotId: null,
    fastMode: false,
  }
}

// All matches, live table state derived from whichever one has status 'live'.
const liveMatches: Match[] = [buildLiveMatch()]

function currentLiveMatch(table: string): Match | null {
  return liveMatches.find((m) => m.table === table && m.status === 'live') ?? null
}

// ---------------------------------------------------------------------------------------------
// Tournaments (2026-08-31 round-8 feedback item 21) — mirrors server/app/services/
// tournament_service.py's shape/behavior (seeding, byes, advancement) in plain TS, so mock mode
// exercises the same flow real matches drive. Starts empty (no active tournament), like a fresh
// DB — nothing seeded here.
// ---------------------------------------------------------------------------------------------
interface MockTournamentParticipant {
  tournamentId: number
  playerId: number
  seed: number | null
  joinedAt: string
}

interface MockTournamentSlot {
  id: number
  tournamentId: number
  round: number
  slotIndex: number
  playerAId: number | null
  playerBId: number | null
  winnerPlayerId: number | null
  matchId: number | null
  isBye: boolean
}

interface MockTournament {
  format: Tournament['format']
  prize: string | null
  knockoutSize: number | null
  matchTypeCode: Tournament['matchTypeCode']
  bestOf: number
  id: number
  name: string
  status: TournamentStatus
  seedingMode: string
  ratingPool: RatingPool
  bracketSize: number | null
  winnerPlayerId: number | null
  createdAt: string
  startedAt: string | null
  finishedAt: string | null
}

let nextTournamentId = 1
let nextTournamentSlotId = 1
const tournaments: MockTournament[] = []
const tournamentParticipants: MockTournamentParticipant[] = []
const tournamentSlots: MockTournamentSlot[] = []

function findTournament(id: number): MockTournament {
  const t = tournaments.find((t) => t.id === id)
  if (!t) throw err('tournament_not_found', 'Unknown tournament')
  return t
}

function nextPow2(n: number): number {
  let p = 1
  while (p < n) p *= 2
  return p
}

// Standard single-elimination seeding order (size a power of two), e.g. size=8 ->
// [1,8,4,5,2,7,3,6] (round-0 pairs 1v8, 4v5, 2v7, 3v6) — mirrors tournament_service._seed_order.
function seedOrder(size: number): number[] {
  let seeds = [1]
  while (seeds.length < size) {
    const n = seeds.length * 2
    seeds = seeds.flatMap((s) => [s, n + 1 - s])
  }
  return seeds
}

function bracketNumRounds(size: number): number {
  return Math.round(Math.log2(size))
}

function serializeTournamentSlot(s: MockTournamentSlot): TournamentBracketSlot {
  return {
    id: s.id,
    round: s.round,
    slotIndex: s.slotIndex,
    playerA: s.playerAId != null ? findPlayer(s.playerAId) : null,
    playerB: s.playerBId != null ? findPlayer(s.playerBId) : null,
    winnerPlayerId: s.winnerPlayerId,
    matchId: s.matchId,
    isBye: s.isBye,
    stage: 'knockout',
  }
}

function serializeTournamentDetail(t: MockTournament): TournamentDetail {
  const participants = tournamentParticipants
    .filter((p) => p.tournamentId === t.id)
    .sort((a, b) => a.joinedAt.localeCompare(b.joinedAt))
    .map((p) => ({ player: findPlayer(p.playerId), seed: p.seed, registeredVia: 'manual' }))
  const slots = tournamentSlots
    .filter((s) => s.tournamentId === t.id)
    .sort((a, b) => a.round - b.round || a.slotIndex - b.slotIndex)
  const rounds: TournamentBracketSlot[][] = []
  for (const s of slots) {
    while (rounds.length <= s.round) rounds.push([])
    rounds[s.round].push(serializeTournamentSlot(s))
  }
  // Mock stays single_elim-only — group phase always empty here.
  return { ...t, participants, rounds, groupRounds: [], standings: [] }
}

// Writes `winnerPlayerId` into round/slotIndex's slot, and — unless this was the final round —
// propagates it into the correct side of the NEXT round's slot; marks the tournament finished if
// it WAS the final. Mirrors tournament_service._advance_winner exactly.
function advanceTournamentWinner(t: MockTournament, round: number, slotIndex: number, winnerPlayerId: number): void {
  const slot = tournamentSlots.find((s) => s.tournamentId === t.id && s.round === round && s.slotIndex === slotIndex)
  if (!slot) return
  slot.winnerPlayerId = winnerPlayerId

  const numRounds = t.bracketSize ? bracketNumRounds(t.bracketSize) : round + 1
  if (round + 1 >= numRounds) {
    t.status = 'finished'
    t.winnerPlayerId = winnerPlayerId
    t.finishedAt = iso(0)
    return
  }
  const nextSlot = tournamentSlots.find(
    (s) => s.tournamentId === t.id && s.round === round + 1 && s.slotIndex === Math.floor(slotIndex / 2),
  )
  if (!nextSlot) return
  if (slotIndex % 2 === 0) nextSlot.playerAId = winnerPlayerId
  else nextSlot.playerBId = winnerPlayerId
}

export async function createTournament(name: string, _format?: string, _prize?: string | null, _mtc?: string, _bo?: number): Promise<Tournament> {
  const clean = name.trim()
  if (!clean) throw err('invalid_name', 'Tournament name must not be empty')
  const t: MockTournament = {
    id: nextTournamentId++,
    name: clean,
    status: 'registration',
    seedingMode: 'elo_seeded',
    format: (_format as Tournament['format']) ?? 'single_elim',
    prize: _prize ?? null,
    knockoutSize: null,
    matchTypeCode: (_mtc as Tournament['matchTypeCode']) ?? 'rapid11',
    bestOf: _bo ?? 3,
    ratingPool: 'rapid',
    bracketSize: null,
    winnerPlayerId: null,
    createdAt: iso(0),
    startedAt: null,
    finishedAt: null,
  }
  tournaments.push(t)
  return snapshot(t)
}

export async function getTournaments(status?: string): Promise<{ items: Tournament[] }> {
  let items = tournaments.slice()
  if (status) items = items.filter((t) => t.status === status)
  items.sort((a, b) => b.createdAt.localeCompare(a.createdAt))
  return { items: snapshot(items) }
}

export async function getTournament(id: number): Promise<TournamentDetail> {
  return snapshot(serializeTournamentDetail(findTournament(id)))
}

export async function addTournamentParticipant(tournamentId: number, playerId: number, _via?: string): Promise<TournamentDetail> {
  const t = findTournament(tournamentId)
  if (t.status !== 'registration') throw err('registration_closed', "This tournament's bracket has already been generated")
  findPlayer(playerId)
  if (tournamentParticipants.some((p) => p.tournamentId === tournamentId && p.playerId === playerId)) {
    throw err('already_registered', 'This player is already registered')
  }
  tournamentParticipants.push({ tournamentId, playerId, seed: null, joinedAt: iso(0) })
  return snapshot(serializeTournamentDetail(t))
}

export async function removeTournamentParticipant(tournamentId: number, playerId: number): Promise<TournamentDetail> {
  const t = findTournament(tournamentId)
  if (t.status !== 'registration') throw err('registration_closed', "This tournament's bracket has already been generated")
  const idx = tournamentParticipants.findIndex((p) => p.tournamentId === tournamentId && p.playerId === playerId)
  if (idx === -1) throw err('not_registered', 'This player is not registered for this tournament')
  tournamentParticipants.splice(idx, 1)
  return snapshot(serializeTournamentDetail(t))
}

export async function generateBracket(tournamentId: number): Promise<TournamentDetail> {
  const t = findTournament(tournamentId)
  if (t.status === 'finished') throw err('tournament_finished', 'This tournament has already finished')

  const existing = tournamentSlots.filter((s) => s.tournamentId === tournamentId)
  if (existing.some((s) => s.matchId != null)) {
    throw err('bracket_already_started', 'Cannot regenerate — a match has already been started')
  }

  const participants = tournamentParticipants.filter((p) => p.tournamentId === tournamentId)
  if (participants.length < 2) throw err('not_enough_participants', 'Need at least 2 participants to generate a bracket')

  const pool = t.ratingPool
  const ratingOf = (playerId: number) => findPlayer(playerId).ratings[pool].rating
  const ordered = participants
    .slice()
    .sort((a, b) => ratingOf(b.playerId) - ratingOf(a.playerId) || a.playerId - b.playerId)
  ordered.forEach((p, i) => (p.seed = i + 1))

  const seedToPlayer = new Map(ordered.map((p) => [p.seed as number, p.playerId]))
  const size = nextPow2(ordered.length)
  const order = seedOrder(size)
  const numRounds = bracketNumRounds(size)
  const roundSizes = Array.from({ length: numRounds }, (_, r) => size / 2 ** (r + 1))

  for (let i = tournamentSlots.length - 1; i >= 0; i--) {
    if (tournamentSlots[i].tournamentId === tournamentId) tournamentSlots.splice(i, 1)
  }

  const created: MockTournamentSlot[][] = []
  for (let r = 0; r < numRounds; r++) {
    const row: MockTournamentSlot[] = []
    for (let i = 0; i < roundSizes[r]; i++) {
      const slot: MockTournamentSlot = {
        id: nextTournamentSlotId++,
        tournamentId,
        round: r,
        slotIndex: i,
        playerAId: null,
        playerBId: null,
        winnerPlayerId: null,
        matchId: null,
        isBye: false,
      }
      tournamentSlots.push(slot)
      row.push(slot)
    }
    created.push(row)
  }

  // Round 0: fill from the seeding order. A seed beyond ordered.length has no player = a bye —
  // exactly one of playerA/playerB being null (never both, see tournament_service.py's header
  // comment for why) means an immediate auto-advance, no match played.
  const byes: Array<[number, number]> = []
  for (let i = 0; i < roundSizes[0]; i++) {
    const playerA = seedToPlayer.get(order[2 * i]) ?? null
    const playerB = seedToPlayer.get(order[2 * i + 1]) ?? null
    const slot = created[0][i]
    slot.playerAId = playerA
    slot.playerBId = playerB
    if (playerA != null && playerB == null) {
      slot.isBye = true
      slot.winnerPlayerId = playerA
      byes.push([i, playerA])
    } else if (playerB != null && playerA == null) {
      slot.isBye = true
      slot.winnerPlayerId = playerB
      byes.push([i, playerB])
    }
  }

  t.status = 'in_progress'
  t.bracketSize = size
  t.startedAt = iso(0)

  for (const [slotIndex, winnerId] of byes) {
    advanceTournamentWinner(t, 0, slotIndex, winnerId)
  }

  return snapshot(serializeTournamentDetail(t))
}

function allMatchesById(id: number): Match | undefined {
  return liveMatches.find((m) => m.id === id) ?? finishedMatches.find((m) => m.id === id)
}

// ---------------------------------------------------------------------------------------------
// Fake SSE — subscribers per table, replayed after every mutating call.
// ---------------------------------------------------------------------------------------------

type Listener = (state: TableState) => void
const subscribers = new Map<string, Set<Listener>>()

function tableState(table: string): TableState {
  return { table, name: table === 'main' ? 'Main table' : table, liveMatch: snapshot(currentLiveMatch(table)) }
}

function broadcast(table: string) {
  const state = tableState(table)
  subscribers.get(table)?.forEach((fn) => fn(state))
}

export function subscribeTableEvents(
  table: string,
  onState: (state: TableState) => void,
  onConnectionChange?: (connected: boolean) => void,
): { close: () => void } {
  let set = subscribers.get(table)
  if (!set) {
    set = new Set()
    subscribers.set(table, set)
  }
  set.add(onState)
  onConnectionChange?.(true)
  // Async so callers can finish wiring up before the first tick, matching real EventSource
  // timing (never synchronous within the subscribing call).
  queueMicrotask(() => onState(tableState(table)))
  return {
    close() {
      set?.delete(onState)
    },
  }
}

// ---------------------------------------------------------------------------------------------
// api.ts-shaped functions
// ---------------------------------------------------------------------------------------------

let scanCounter = 0

export async function identityResolve(_scan: string): Promise<IdentityResolveResponse> {
  const p = players[scanCounter % players.length]
  scanCounter++
  p.lastSeenAt = iso(0)
  return { player: snapshot(p), source: 'kombo' }
}

export async function getPlayers(q = '', limit = 50): Promise<{ items: Player[] }> {
  const needle = q.trim().toLowerCase()
  const items = players
    .filter((p) => !needle || p.displayName.toLowerCase().includes(needle) || (p.email ?? '').toLowerCase().includes(needle))
    .slice()
    .sort((a, b) => b.lastSeenAt.localeCompare(a.lastSeenAt))
    .slice(0, limit)
  return { items: snapshot(items) }
}

function ratingHistoryFor(playerId: number, pool: RatingPool): RatingHistoryEntry[] {
  const relevant = [...liveMatches, ...finishedMatches]
    .filter((m) => m.status === 'finished' && m.matchType.ratingPool === pool && m.ratingChanges?.[playerId])
    .sort((a, b) => (a.finishedAt ?? '').localeCompare(b.finishedAt ?? ''))
  return relevant.map((m) => {
    const rc = m.ratingChanges![playerId]
    return { matchId: m.id, at: m.finishedAt ?? m.startedAt, before: rc.before, after: rc.after }
  })
}

export async function createGuest(displayName: string): Promise<Player> {
  const player: Player = {
    id: 1000 + players.length,
    displayName,
    firstName: null,
    lastName: null,
    email: null,
    pictureUrl: null,
    isGuest: true,
    identitySource: 'guest',
    createdAt: new Date().toISOString(),
    lastSeenAt: new Date().toISOString(),
    ratings: { rapid: unranked(), classical: unranked() },
  }
  players.push(player)
  return player
}

// Round-4 feedback item 2 (Player profile) — rank-within-pool + top rivals, mirroring the real
// backend's player_service.get_player_rank/get_top_rivals so the mock behaves the same shape.
function rankFor(id: number, pool: RatingPool): number | null {
  const r = findPlayer(id).ratings[pool]
  if (r.matches <= 0) return null
  const higher = players.filter((p) => p.ratings[pool].matches > 0 && p.ratings[pool].rating > r.rating).length
  return higher + 1
}

function topRivalsFor(id: number, limit = 5): PlayerDetail['topRivals'] {
  const finished = [...liveMatches, ...finishedMatches].filter(
    (m) => m.status === 'finished' && (m.players.left.id === id || m.players.right.id === id),
  )
  const agg = new Map<number, { matches: number; wins: number; losses: number }>()
  for (const m of finished) {
    const opponentId = m.players.left.id === id ? m.players.right.id : m.players.left.id
    const entry = agg.get(opponentId) ?? { matches: 0, wins: 0, losses: 0 }
    entry.matches += 1
    if (m.winnerPlayerId === id) entry.wins += 1
    else if (m.winnerPlayerId != null) entry.losses += 1
    agg.set(opponentId, entry)
  }
  return [...agg.entries()]
    .sort((a, b) => b[1].matches - a[1].matches)
    .slice(0, limit)
    .map(([opponentId, stats]) => ({ player: findPlayer(opponentId), ...stats }))
}

export async function getPlayer(id: number): Promise<PlayerDetail> {
  const p = findPlayer(id)
  const recentMatches = [...liveMatches, ...finishedMatches]
    .filter((m) => m.players.left.id === id || m.players.right.id === id)
    .sort((a, b) => (b.finishedAt ?? b.startedAt).localeCompare(a.finishedAt ?? a.startedAt))
    .slice(0, 20)
  return snapshot({
    ...p,
    recentMatches,
    ratingHistory: { rapid: ratingHistoryFor(id, 'rapid'), classical: ratingHistoryFor(id, 'classical') },
    rank: { rapid: rankFor(id, 'rapid'), classical: rankFor(id, 'classical') },
    topRivals: topRivalsFor(id),
  })
}

export async function getMatchTypes(): Promise<{ items: MatchType[] }> {
  return { items: snapshot(matchTypes) }
}

export async function createMatch(payload: _CreateMatchPayload): Promise<Match> {
  const table = payload.table ?? 'main'
  if (currentLiveMatch(table)) throw err('table_busy', 'This table already has a live match')
  if (payload.leftPlayerId === payload.rightPlayerId) throw err('same_player', 'Both sides have the same player')
  const mt = findMatchType(payload.matchTypeCode)
  if (!mt.allowedBestOf.includes(payload.bestOf)) throw err('bad_best_of', 'bestOf not allowed for this match type')
  if (payload.serveEvery != null && !SERVE_EVERY_OPTIONS.includes(payload.serveEvery)) {
    throw err('invalid_serve_every', 'serveEvery must be one of ' + SERVE_EVERY_OPTIONS.join(', '))
  }
  // Round-8 feedback item 21 — validated BEFORE constructing the match, same as the checks
  // above, so a rejected tournament link doesn't burn a match id. Mirrors
  // tournament_service.attach_match_to_slot on the real backend.
  let tournamentSlot: MockTournamentSlot | undefined
  if (payload.tournamentSlotId != null) {
    tournamentSlot = tournamentSlots.find((s) => s.id === payload.tournamentSlotId)
    if (!tournamentSlot) throw err('tournament_slot_not_found', 'Unknown tournament bracket slot')
    if (tournamentSlot.matchId != null || tournamentSlot.winnerPlayerId != null) {
      throw err('slot_already_started', 'This bracket slot already has a result')
    }
    if (tournamentSlot.playerAId == null || tournamentSlot.playerBId == null) {
      throw err('slot_not_ready', 'This bracket slot is still waiting on an earlier result')
    }
    const expected = new Set([tournamentSlot.playerAId, tournamentSlot.playerBId])
    if (!expected.has(payload.leftPlayerId) || !expected.has(payload.rightPlayerId)) {
      throw err('player_mismatch', "The two players don't match this bracket slot")
    }
  }
  const left = findPlayer(payload.leftPlayerId)
  const right = findPlayer(payload.rightPlayerId)
  const firstServer = payload.firstServer ?? 'left'
  const match: Match = {
    id: nextMatchId++,
    table,
    matchType: mt,
    bestOf: payload.bestOf,
    serveEvery: payload.serveEvery ?? DEFAULT_SERVE_EVERY,
    status: 'live',
    startedAt: iso(0),
    finishedAt: null,
    players: { left, right },
    currentPlayers: { left, right },
    games: [emptyGame(0, left, right, firstServer, 0)],
    currentGameIndex: 0,
    gamesWon: {},
    // Round-31 (2026-08-31): fast mode never tracks who's serving (no points to derive it
    // from) — null, same as a finished/abandoned match, mirroring the real backend.
    server: payload.fastMode ? null : firstServer,
    serverPlayerId: payload.fastMode ? null : firstServer === 'left' ? left.id : right.id,
    banner: null,
    winnerPlayerId: null,
    ratingChanges: null,
    pointsCount: 0,
    lastPoint: null,
    tournamentId: null,
    tournamentSlotId: null,
    fastMode: payload.fastMode ?? false,
  }
  if (tournamentSlot) tournamentSlot.matchId = match.id
  liveMatches.push(match)
  broadcast(table)
  return snapshot(match)
}

export async function getMatches(params: GetMatchesParams = {}): Promise<{ items: Match[]; total: number }> {
  let items = [...liveMatches, ...finishedMatches]
  if (params.status) items = items.filter((m) => m.status === params.status)
  if (params.table) items = items.filter((m) => m.table === params.table)
  if (params.playerId != null) items = items.filter((m) => m.players.left.id === params.playerId || m.players.right.id === params.playerId)
  items = items.slice().sort((a, b) => (b.finishedAt ?? b.startedAt).localeCompare(a.finishedAt ?? a.startedAt))
  const total = items.length
  const offset = params.offset ?? 0
  const limit = params.limit ?? 50
  return { items: snapshot(items.slice(offset, offset + limit)), total }
}

export async function getMatch(id: number): Promise<Match> {
  const m = allMatchesById(id)
  if (!m) throw err('match_not_found', 'Unknown match')
  return snapshot(m)
}

function finishGameIfNaive(match: Match) {
  const mt = match.matchType
  const game = match.games[match.currentGameIndex!]
  const { left, right } = game.scores
  const hi = Math.max(left, right)
  const lo = Math.min(left, right)
  if (hi >= mt.pointsToWin && hi - lo >= mt.winBy) {
    game.status = 'finished'
    game.finishedAt = iso(0)
    const winnerSide: Side = left > right ? 'left' : 'right'
    const winnerId = game.players[winnerSide].id
    game.winnerPlayerId = winnerId
    match.gamesWon[winnerId] = (match.gamesWon[winnerId] ?? 0) + 1

    const gamesToWin = Math.floor(match.bestOf / 2) + 1
    if ((match.gamesWon[winnerId] ?? 0) >= gamesToWin) {
      match.status = 'finished'
      match.finishedAt = iso(0)
      match.winnerPlayerId = winnerId
      match.currentGameIndex = null
      match.server = null
      match.serverPlayerId = null
      const loserId = winnerSide === 'left' ? game.players.right.id : game.players.left.id
      const winner = findPlayer(winnerId)
      const loser = findPlayer(loserId)
      const pool = mt.ratingPool
      const winBefore = winner.ratings[pool].rating
      const loseBefore = loser.ratings[pool].rating
      winner.ratings[pool] = ranked(winBefore + 12, Math.max(30, winner.ratings[pool].rd - 5), winner.ratings[pool].volatility, winner.ratings[pool].matches + 1, winner.ratings[pool].wins + 1, winner.ratings[pool].losses)
      loser.ratings[pool] = ranked(loseBefore - 12, Math.max(30, loser.ratings[pool].rd - 5), loser.ratings[pool].volatility, loser.ratings[pool].matches + 1, loser.ratings[pool].wins, loser.ratings[pool].losses + 1)
      match.ratingChanges = {
        [winner.id]: { pool, before: winBefore, after: winner.ratings[pool].rating, delta: winner.ratings[pool].rating - winBefore },
        [loser.id]: { pool, before: loseBefore, after: loser.ratings[pool].rating, delta: loser.ratings[pool].rating - loseBefore },
      }

      // Round-8 feedback item 21: if this match was started from a tournament bracket slot,
      // auto-advance the winner — mirrors match_service's real-backend hook into
      // tournament_service.on_match_finished.
      const linkedSlot = tournamentSlots.find((s) => s.matchId === match.id)
      if (linkedSlot) {
        const t = tournaments.find((t) => t.id === linkedSlot.tournamentId)
        if (t) advanceTournamentWinner(t, linkedSlot.round, linkedSlot.slotIndex, winnerId)
      }
    } else {
      // Naive end-switch every game, like the real engine (no deuce/mid-decider logic here).
      const nextIndex = game.index + 1
      const flipped = { left: match.currentPlayers.right, right: match.currentPlayers.left }
      match.currentPlayers = flipped
      const nextFirstServer: Side = winnerSide === 'left' ? 'right' : 'left'
      match.games.push(emptyGame(nextIndex, flipped.left, flipped.right, nextFirstServer, 0))
      match.currentGameIndex = nextIndex
      match.server = nextFirstServer
      match.serverPlayerId = nextFirstServer === 'left' ? flipped.left.id : flipped.right.id
      match.banner = 'switch_ends'
    }
  }
}

export async function postPoint(matchId: number, side: Side, source: 'touch' | 'admin' | 'voice' = 'touch'): Promise<Match> {
  const match = liveMatches.find((m) => m.id === matchId)
  if (!match || match.status !== 'live') throw err('match_not_live', 'Match is not live')
  match.banner = null
  const game = match.games[match.currentGameIndex!]
  game.scores[side] += 1
  match.pointsCount += 1
  match.lastPoint = { side, source, at: iso(0) }
  finishGameIfNaive(match)
  broadcast(match.table)
  return snapshot(match)
}

export async function undoPoint(matchId: number): Promise<Match> {
  const match = liveMatches.find((m) => m.id === matchId)
  if (!match || match.status !== 'live') throw err('match_not_live', 'Match is not live')
  if (match.pointsCount === 0) throw err('nothing_to_undo', 'No points to undo')
  const game = match.games[match.currentGameIndex!]
  const side: Side = match.lastPoint?.side ?? (game.scores.left >= game.scores.right ? 'left' : 'right')
  if (game.scores[side] > 0) game.scores[side] -= 1
  match.pointsCount -= 1
  match.lastPoint = null
  broadcast(match.table)
  return snapshot(match)
}

export async function abandonMatch(matchId: number): Promise<Match> {
  const match = liveMatches.find((m) => m.id === matchId)
  if (!match || match.status !== 'live') throw err('match_not_live', 'Match is not live')
  match.status = 'abandoned'
  match.finishedAt = iso(0)
  match.currentGameIndex = null
  match.server = null
  match.serverPlayerId = null
  broadcast(match.table)
  return snapshot(match)
}

export async function swapSides(matchId: number): Promise<Match> {
  const match = liveMatches.find((m) => m.id === matchId)
  if (!match || match.status !== 'live') throw err('match_not_live', 'Match is not live')
  // Round-31 (2026-08-31): fast mode has no points to gate on — the equivalent "nothing
  // played yet" check is no FINISHED game yet (mirrors match_service.swap_sides server-side).
  const alreadyPlayed = match.fastMode ? match.games.some((g) => g.status === 'finished') : match.pointsCount !== 0
  if (alreadyPlayed) throw err('cannot_swap', 'Cannot swap sides after points have been scored')
  const flip = { left: match.players.right, right: match.players.left }
  match.players = flip
  match.currentPlayers = flip
  match.games[0].players = flip
  if (match.fastMode) {
    match.server = null
    match.serverPlayerId = null
  } else {
    match.server = match.server === 'left' ? 'right' : 'left'
    match.serverPlayerId = match.server === 'left' ? flip.left.id : flip.right.id
    match.games[0].firstServer = match.server
  }
  broadcast(match.table)
  return snapshot(match)
}

// Round-31 (2026-08-31) — fast mode's only scoring call: finalize the CURRENT game with its
// two final scores directly (DESIGN.md §23), no point-by-point increments. Mirrors
// finishGameIfNaive's between-game bookkeeping (naive full flip + next placeholder game) but
// assigns the final score in one shot instead of looping postPoint, and skips the mid-game
// deciding-game switch the point path fakes (fast mode has no "half-way through" to model).
export async function finalizeGameFast(matchId: number, leftScore: number, rightScore: number): Promise<Match> {
  const match = liveMatches.find((m) => m.id === matchId)
  if (!match || match.status !== 'live') throw err('match_not_live', 'Match is not live')
  if (!match.fastMode) throw err('not_fast_mode', "This match isn't using fast score entry")

  const mt = match.matchType
  if (leftScore < 0 || rightScore < 0 || leftScore === rightScore) {
    throw err('invalid_score', 'Invalid score')
  }
  const hi = Math.max(leftScore, rightScore)
  const lo = Math.min(leftScore, rightScore)
  const threshold = mt.pointsToWin - mt.winBy
  const ok = lo <= threshold ? hi === mt.pointsToWin : hi - lo === mt.winBy
  if (!ok) {
    throw err('invalid_score', `${leftScore}-${rightScore} isn't a valid result for a race to ${mt.pointsToWin} (win by ${mt.winBy})`)
  }

  const game = match.games[match.currentGameIndex ?? match.games.length - 1]
  game.scores = { left: leftScore, right: rightScore }
  game.status = 'finished'
  game.finishedAt = iso(0)
  const winnerSide: Side = leftScore > rightScore ? 'left' : 'right'
  const winnerId = game.players[winnerSide].id
  game.winnerPlayerId = winnerId
  match.gamesWon[winnerId] = (match.gamesWon[winnerId] ?? 0) + 1

  const gamesToWin = Math.floor(match.bestOf / 2) + 1
  if ((match.gamesWon[winnerId] ?? 0) >= gamesToWin) {
    match.status = 'finished'
    match.finishedAt = iso(0)
    match.winnerPlayerId = winnerId
    match.currentGameIndex = null
    match.server = null
    match.serverPlayerId = null
    const loserId = winnerSide === 'left' ? game.players.right.id : game.players.left.id
    const winner = findPlayer(winnerId)
    const loser = findPlayer(loserId)
    const pool = mt.ratingPool
    const winBefore = winner.ratings[pool].rating
    const loseBefore = loser.ratings[pool].rating
    winner.ratings[pool] = ranked(winBefore + 12, Math.max(30, winner.ratings[pool].rd - 5), winner.ratings[pool].volatility, winner.ratings[pool].matches + 1, winner.ratings[pool].wins + 1, winner.ratings[pool].losses)
    loser.ratings[pool] = ranked(loseBefore - 12, Math.max(30, loser.ratings[pool].rd - 5), loser.ratings[pool].volatility, loser.ratings[pool].matches + 1, loser.ratings[pool].wins, loser.ratings[pool].losses + 1)
    match.ratingChanges = {
      [winner.id]: { pool, before: winBefore, after: winner.ratings[pool].rating, delta: winner.ratings[pool].rating - winBefore },
      [loser.id]: { pool, before: loseBefore, after: loser.ratings[pool].rating, delta: loser.ratings[pool].rating - loseBefore },
    }
    const linkedSlot = tournamentSlots.find((s) => s.matchId === match.id)
    if (linkedSlot) {
      const t = tournaments.find((t) => t.id === linkedSlot.tournamentId)
      if (t) advanceTournamentWinner(t, linkedSlot.round, linkedSlot.slotIndex, winnerId)
    }
  } else {
    const nextIndex = game.index + 1
    const flipped = { left: match.currentPlayers.right, right: match.currentPlayers.left }
    match.currentPlayers = flipped
    const nextFirstServer: Side = winnerSide === 'left' ? 'right' : 'left'
    match.games.push(emptyGame(nextIndex, flipped.left, flipped.right, nextFirstServer, 0))
    match.currentGameIndex = nextIndex
    match.server = null
    match.serverPlayerId = null
  }

  broadcast(match.table)
  return snapshot(match)
}

export async function getTable(table: string): Promise<TableState> {
  return tableState(table)
}

export async function getRankings(pool: RatingPool): Promise<RankingsResponse> {
  const entries = players
    .filter((p) => p.ratings[pool].matches > 0)
    .map((p) => ({ p, r: p.ratings[pool] }))
    .sort((a, b) => b.r.rating - a.r.rating)
    .map(({ p, r }, i) => ({
      rank: i + 1,
      player: p,
      rating: r.rating,
      rd: r.rd,
      matches: r.matches,
      wins: r.wins,
      losses: r.losses,
      winRate: r.matches > 0 ? r.wins / r.matches : 0,
      provisional: r.provisional,
      lastMatchAt: p.lastSeenAt,
    }))
  return snapshot({ pool, entries })
}

export async function getVersus(a: number, b: number): Promise<VersusResponse> {
  const playerA = findPlayer(a)
  const playerB = findPlayer(b)
  const recent = [...liveMatches, ...finishedMatches]
    .filter((m) => {
      const ids = [m.players.left.id, m.players.right.id]
      return ids.includes(a) && ids.includes(b)
    })
    .sort((x, y) => (y.finishedAt ?? y.startedAt).localeCompare(x.finishedAt ?? x.startedAt))
    .slice(0, 20)

  const pools: VersusResponse['pools'] = {}
  for (const pool of ['rapid', 'classical'] as RatingPool[]) {
    const poolMatches = recent.filter((m) => m.matchType.ratingPool === pool && m.status === 'finished')
    let winsA = 0,
      winsB = 0,
      gamesA = 0,
      gamesB = 0,
      pointsA = 0,
      pointsB = 0
    for (const m of poolMatches) {
      if (m.winnerPlayerId === a) winsA++
      else if (m.winnerPlayerId === b) winsB++
      for (const g of m.games) {
        const gAisLeft = g.players.left.id === a
        const scoreA = gAisLeft ? g.scores.left : g.scores.right
        const scoreB = gAisLeft ? g.scores.right : g.scores.left
        pointsA += scoreA
        pointsB += scoreB
        if (g.winnerPlayerId === a) gamesA++
        else if (g.winnerPlayerId === b) gamesB++
      }
    }
    pools[pool] = {
      matches: poolMatches.length,
      winsA,
      winsB,
      gamesA,
      gamesB,
      pointsA,
      pointsB,
      lastMatchAt: poolMatches[0]?.finishedAt ?? null,
    }
  }

  return snapshot({ a: playerA, b: playerB, pools, recent })
}

function firstServeCount(playerId: number): number {
  return [...liveMatches, ...finishedMatches].filter((m) => {
    const firstServeSide = m.games[0]?.firstServer
    if (!firstServeSide) return false
    return m.players[firstServeSide].id === playerId
  }).length
}

export async function getHeadToHead(a: number, b: number): Promise<HeadToHeadResponse> {
  findPlayer(a)
  findPlayer(b)
  const relevant = [...liveMatches, ...finishedMatches].filter((m) => {
    if (m.status !== 'finished') return false
    const ids = [m.players.left.id, m.players.right.id]
    return ids.includes(a) && ids.includes(b)
  })
  const matches = relevant.length
  const winsA = relevant.filter((m) => m.winnerPlayerId === a).length
  const winsB = relevant.filter((m) => m.winnerPlayerId === b).length
  const firstServeCountA = firstServeCount(a)
  const firstServeCountB = firstServeCount(b)
  const suggestedFirstServerPlayerId =
    firstServeCountA < firstServeCountB ? a : firstServeCountB < firstServeCountA ? b : Math.min(a, b)
  return {
    aPlayerId: a,
    bPlayerId: b,
    matches,
    winsA,
    winsB,
    winRateA: matches ? winsA / matches : 0,
    winRateB: matches ? winsB / matches : 0,
    firstServeCountA,
    firstServeCountB,
    suggestedFirstServerPlayerId,
  }
}

// Voice (round-15) — the mock keeps an in-memory corpus so "Train my voice" can be clicked
// through offline; no fingerprint math happens here (voiceScore.ts is pure and tested via the
// real path).
import type {
  RollCallInteractionPayload,
  TtsCacheEntry,
  TtsEvent,
  TtsTemplate,
  TtsVoice,
  VoiceConfirmationInteractionPayload,
  VoiceProfile,
  VoiceSampleOut,
  VoiceSamplePayload,
  VoiceTrainingProgress,
  VoiceTrainingProgressPayload,
} from './types'

let voiceSampleId = 0
const voiceProfiles = new Map<number, VoiceProfile>()
// Item 4 (round-18): keyed by `${playerId}:${lang}`, same shape as the server's composite PK.
const trainingProgress = new Map<string, VoiceTrainingProgress>()

export async function postVoiceSample(payload: VoiceSamplePayload): Promise<VoiceSampleOut> {
  if (payload.playerId != null && payload.spkVector) {
    const existing = voiceProfiles.get(payload.playerId)
    voiceProfiles.set(payload.playerId, { playerId: payload.playerId, sampleCount: (existing?.sampleCount ?? 0) + 1 })
  }
  return { id: ++voiceSampleId, playerId: payload.playerId ?? null, hasAudio: !!payload.audioB64, hasVector: !!payload.spkVector }
}

export async function getVoiceProfiles(): Promise<{ items: VoiceProfile[] }> {
  return { items: [...voiceProfiles.values()] }
}

export async function rebuildVoiceProfile(playerId: number): Promise<VoiceProfile> {
  const p = voiceProfiles.get(playerId)
  if (!p) throw new ApiRequestError({ error: 'No samples', code: 'no_vectors' }, 400)
  return p
}

export async function getTrainingProgress(playerId: number): Promise<{ items: VoiceTrainingProgress[] }> {
  return { items: [...trainingProgress.values()].filter((p) => p.playerId === playerId) }
}

export async function putTrainingProgress(payload: VoiceTrainingProgressPayload): Promise<VoiceTrainingProgress> {
  const row: VoiceTrainingProgress = { ...payload }
  trainingProgress.set(`${payload.playerId}:${payload.lang}`, row)
  return row
}

// Round-27 (2026-08-31): no TTS backend to mock against — the URL is still built (so
// services/confirmFlow.ts's <audio> element gets a src), it just 404s under VITE_MOCK, which
// the flow already treats as "TTS unavailable, fall back to on-screen-only" (same as a real 204).
export function voiceConfirmNameTtsUrl(playerId: number, lang: string): string {
  return `/api/voice/tts/confirm-name/${playerId}?lang=${encodeURIComponent(lang)}`
}

export async function postVoiceConfirmationInteraction(_payload: VoiceConfirmationInteractionPayload): Promise<void> {
  // Best-effort observability write in the real backend — a no-op here, consistent with the mock
  // not modeling server-side persistence beyond what other mock functions above already keep.
}

// Round-29 (2026-08-31) — tournament roll-call, same mock conventions as the confirmation flow.
export function rollCallTtsUrl(playerId: number, lang: string): string {
  return `/api/voice/tts/roll-call/${playerId}?lang=${encodeURIComponent(lang)}`
}

export async function postRollCallInteraction(_payload: RollCallInteractionPayload): Promise<void> {
  // Best-effort observability write in the real backend — a no-op here.
}

// Round-30 (2026-09-01) — TTS debug panel. No TTS backend to mock against under VITE_MOCK, same
// as the confirm-name/roll-call URLs above — URLs are still built (so the panel's fetch has
// somewhere to point) and just fail, which the panel already surfaces as "TTS unavailable".
const mockTtsTemplates: TtsTemplate[] = [
  { id: 'confirm_name', label: 'Corner-claim confirmation — "¿Eres {nombre}?"', flow: 'confirm_name' },
  { id: 'roll_call', label: 'Tournament roll-call — "{nombre}, ¿estás listo?"', flow: 'roll_call' },
]

export async function getTtsVoices(): Promise<{ items: TtsVoice[] }> {
  return { items: [] }
}

export async function getTtsTemplates(): Promise<{ items: TtsTemplate[] }> {
  return { items: mockTtsTemplates }
}

export function ttsPreviewUrl(templateId: string, playerId: number, lang: string, voice?: string | null): string {
  const params = new URLSearchParams({ lang })
  if (voice) params.set('voice', voice)
  return `/api/voice/tts/preview/${encodeURIComponent(templateId)}/${playerId}?${params}`
}

export function ttsSpeakUrl(text: string, lang: string, voice?: string | null): string {
  const params = new URLSearchParams({ text, lang })
  if (voice) params.set('voice', voice)
  return `/api/voice/tts/speak?${params}`
}

export async function getTtsCache(): Promise<{ items: TtsCacheEntry[] }> {
  return { items: [] }
}

export function ttsCacheAudioUrl(cacheId: string): string {
  return `/api/voice/tts/cache/${encodeURIComponent(cacheId)}/audio`
}

export async function purgeTtsCacheEntry(_cacheId: string, _adminToken: string): Promise<{ purged: number }> {
  return { purged: 0 }
}

export async function purgeAllTtsCache(_adminToken: string): Promise<{ purged: number }> {
  return { purged: 0 }
}

export async function getTtsEvents(_limit = 50): Promise<{ items: TtsEvent[] }> {
  return { items: [] }
}
